import { Component, ElementRef, HostListener, OnDestroy, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { ContentReadyEvent, ShownEvent } from 'devextreme/ui/popup';
import { ThomasAppConfig } from './thomas-app.config';
import { ThomasChatComponent } from './thomas-chat.component';
import { ThomasConversationService } from './thomas-conversation.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- shared Thomas markup uses the js- prefix in every app
  selector: 'js-thomas-widget',
  templateUrl: './thomas-widget.component.html',
  // styles are global (see angular.json) - too large for the component style budget
  encapsulation: ViewEncapsulation.None,
  imports: [DxPopupComponent, DxTemplateDirective, ThomasChatComponent]
})
export class ThomasWidgetComponent implements OnDestroy {

  readonly wrapperAttr = { class: 'thomas-popup' };
  private readonly desktopPosition = { my: 'right top', at: 'right top', of: window, offset: '-12 52' } as const;
  private readonly phonePosition = { my: 'left top', at: 'left top', of: window } as const;

  @ViewChild(DxPopupComponent) private popup?: DxPopupComponent;
  @ViewChild('launcher') private launcher?: ElementRef<HTMLButtonElement>;
  @ViewChild(ThomasChatComponent) private chat?: ThomasChatComponent;
  private content: HTMLElement | null = null;
  private resizeTimer?: ReturnType<typeof setTimeout>;

  thomas = inject(ThomasConversationService);
  private app = inject(ThomasAppConfig);

  onShown(e: ShownEvent) {
    this.onContentReady(e);
    this.chat?.reveal();
  }

  onContentReady(e: ContentReadyEvent) {
    this.content = e.component.content() as HTMLElement;
    this.content.parentElement!.setAttribute('aria-label', 'Truth Engine AI Assistant');
  }

  onHidden() {
    this.content = null;
    this.launcher?.nativeElement.focus();
  }

  newConversation() {
    this.thomas.reset();
    this.chat?.focusComposer();
  }

  get phoneSize(): boolean {
    return this.app.phoneSize;
  }

  // dxPopup's iOS slide animation ends at `position` even when fullScreen, so on phones the anchor must be the window's top-left
  get position() {
    return this.phoneSize ? this.phonePosition : this.desktopPosition;
  }

  @HostListener('window:resize')
  onWindowResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.keepInViewport(), 150);
  }

  // dxPopup keeps a dragged/resized panel at its old pixel position when the window shrinks; re-anchor it if it no longer fits
  private keepInViewport() {
    if (!this.thomas.open || !this.content || this.phoneSize) {
      return;
    }
    const rect = this.content.parentElement!.getBoundingClientRect();
    if (rect.left < 0 || rect.top < 0 || rect.right > window.innerWidth || rect.bottom > window.innerHeight) {
      this.popup?.instance.option('position', { ...this.position });
      this.popup?.instance.repaint();
    }
  }

  ngOnDestroy() {
    clearTimeout(this.resizeTimer);
    this.content = null;
  }
}
